import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { FullAsinWithAudits } from "../../../from-server/asin-central/model";
import { AppThunk } from "../../app/store";
import { createAsinMarketplaceValidationDetailPassExceptions } from "../../app/api";
import { createNotificationDefinition } from "../../notification/notificationConnector";
import { triggerValidationDashboardRefresh } from "../asinValidationTableConnector";
import {
  ValidationAttribute,
  ValidationAttributeDetailStep,
} from "../validationAttribute";
import { runWithConcurrency } from "../../../util/concurrency";

export interface BulkPassExceptionRow {
  readonly key: string;
  readonly asin: string;
  readonly obfuscatedMarketplaceId: string;
  readonly marketplace: string;
  readonly programName: string;
  readonly service: "LVS" | "Hattrick";
  readonly validationId: string;
  readonly step: string;
  readonly reason: string;
}

export const MAX_BULK_PASS_EXCEPTION_ASINS = 1_000;
export const SUBMIT_CONCURRENCY = 8;

export type PassExceptionMode = "regular" | "one_time";

export interface SubmitPassExceptionsState {
  readonly isOpen: boolean;
  readonly mode: PassExceptionMode;
  readonly workingAsins: FullAsinWithAudits[];
  readonly isFetching: boolean;
  readonly fetchError: string | undefined;
  // Whether the working set was capped at MAX_BULK_PASS_EXCEPTION_ASINS
  // during the pagination fetch. Surfaced as a warning in the modal so
  // the user knows they'd only be submitting a partial slice.
  readonly fetchTruncated: boolean;
  readonly isSubmitting: boolean;
  readonly submissionCompleted: boolean;
  // `submissionCompleted` marks the async work as done. `submissionSucceeded`
  // reports whether at least one group succeeded, so the modal can decide
  // whether to auto-close (some success -> close) or stay open (total
  // failure -> keep selection visible for retry).
  readonly submissionSucceeded: boolean;
}

const initialState: SubmitPassExceptionsState = {
  isOpen: false,
  mode: "regular",
  workingAsins: [],
  isFetching: false,
  fetchError: undefined,
  fetchTruncated: false,
  isSubmitting: false,
  submissionCompleted: false,
  submissionSucceeded: false,
};

const submitPassExceptionsSlice = createSlice({
  name: "submitPassExceptions",
  initialState,
  reducers: {
    openWithAsins(
      state,
      action: PayloadAction<{
        readonly asins: FullAsinWithAudits[];
        readonly mode: PassExceptionMode;
      }>
    ) {
      state.isOpen = true;
      state.mode = action.payload.mode;
      state.workingAsins = action.payload.asins;
      state.isFetching = false;
      state.fetchError = undefined;
      state.fetchTruncated = false;
      state.isSubmitting = false;
      state.submissionCompleted = false;
      state.submissionSucceeded = false;
    },
    openAndFetch(
      state,
      action: PayloadAction<{ readonly mode: PassExceptionMode }>
    ) {
      state.isOpen = true;
      state.mode = action.payload.mode;
      state.workingAsins = [];
      state.isFetching = true;
      state.fetchError = undefined;
      state.fetchTruncated = false;
      state.isSubmitting = false;
      state.submissionCompleted = false;
      state.submissionSucceeded = false;
    },
    fetchSucceeded(
      state,
      action: PayloadAction<{
        readonly asins: FullAsinWithAudits[];
        readonly truncated: boolean;
      }>
    ) {
      state.workingAsins = action.payload.asins;
      state.isFetching = false;
      state.fetchError = undefined;
      state.fetchTruncated = action.payload.truncated;
    },
    fetchFailed(state, action: PayloadAction<{ readonly message: string }>) {
      state.workingAsins = [];
      state.isFetching = false;
      state.fetchError = action.payload.message;
      state.fetchTruncated = false;
    },
    submissionStarted(state) {
      state.isSubmitting = true;
      state.submissionCompleted = false;
      state.submissionSucceeded = false;
    },
    submissionFinished(
      state,
      action: PayloadAction<{ readonly hadSuccess: boolean }>
    ) {
      state.isSubmitting = false;
      state.submissionCompleted = true;
      state.submissionSucceeded = action.payload.hadSuccess;
    },
    closeModal() {
      return initialState;
    },
  },
});

export const {
  openWithAsins,
  openAndFetch,
  fetchSucceeded,
  fetchFailed,
  submissionStarted,
  submissionFinished,
  closeModal,
} = submitPassExceptionsSlice.actions;

export default submitPassExceptionsSlice.reducer;

function safeParseValidationAttribute(
  raw: string | undefined
): ValidationAttribute | undefined {
  if (!raw) {
    return undefined;
  }
  try {
    return JSON.parse(unescape(raw)) as ValidationAttribute;
  } catch {
    return undefined;
  }
}

const SERVICES: ReadonlyArray<{
  readonly service: "LVS" | "Hattrick";
  readonly detailKey: keyof FullAsinWithAudits;
  readonly validationIdKey: keyof FullAsinWithAudits;
}> = [
  {
    service: "LVS",
    detailKey: "validationDetailResultLVS",
    validationIdKey: "validationRequestLVS",
  },
  {
    service: "Hattrick",
    detailKey: "validationDetailResultHattrick",
    validationIdKey: "validationRequestHattrick",
  },
];

export function buildBulkPassExceptionRows(
  asins: readonly FullAsinWithAudits[]
): BulkPassExceptionRow[] {
  const rows: BulkPassExceptionRow[] = [];
  for (const asin of asins) {
    for (const svc of SERVICES) {
      const detailRaw = asin[svc.detailKey] as string | undefined;
      const parsed = safeParseValidationAttribute(detailRaw);
      if (!parsed || !Array.isArray(parsed.detail)) {
        continue;
      }
      const validationId =
        (asin[svc.validationIdKey] as string | undefined) ?? "";
      for (const step of parsed.detail) {
        if (step.result !== "fail") {
          continue;
        }
        rows.push({
          key: `${asin.asin}::${asin.marketplace}::${svc.service}::${step.step}`,
          asin: asin.asin,
          obfuscatedMarketplaceId: asin.obfuscatedMarketplaceId,
          marketplace: asin.marketplace,
          programName: asin.programName ?? "",
          service: svc.service,
          validationId,
          step: step.step,
          reason: step.reason ?? "",
        });
      }
    }
  }
  return rows;
}

interface GroupKey {
  readonly asin: string;
  readonly obfuscatedMarketplaceId: string;
  readonly marketplace: string;
  readonly service: "LVS" | "Hattrick";
  readonly validationId: string;
}

function groupRowsForSubmission(
  rows: readonly BulkPassExceptionRow[],
  reason: string,
  mode: PassExceptionMode
): Array<{
  readonly key: GroupKey;
  readonly steps: ValidationAttributeDetailStep[];
}> {
  const buckets = new Map<
    string,
    { key: GroupKey; steps: ValidationAttributeDetailStep[] }
  >();
  const result =
    mode === "one_time" ? "pass_exception_one_time" : "pass_exception";
  for (const row of rows) {
    const bucketKey = `${row.asin}::${row.marketplace}::${row.service}`;
    let bucket = buckets.get(bucketKey);
    if (!bucket) {
      bucket = {
        key: {
          asin: row.asin,
          obfuscatedMarketplaceId: row.obfuscatedMarketplaceId,
          marketplace: row.marketplace,
          service: row.service,
          validationId: row.validationId,
        },
        steps: [],
      };
      buckets.set(bucketKey, bucket);
    }
    bucket.steps.push({
      step: row.step,
      result,
      passExceptionReason: reason,
      reason: row.reason,
    } as ValidationAttributeDetailStep);
  }
  return Array.from(buckets.values());
}

function pluralSubmissions(n: number): string {
  return n === 1 ? "1 submission" : `${n} submissions`;
}

export const submitBulkPassExceptions =
  (
    selectedRows: readonly BulkPassExceptionRow[],
    reason: string,
    mode: PassExceptionMode
  ): AppThunk =>
  async (dispatch) => {
    dispatch(submissionStarted());
    const groups = groupRowsForSubmission(selectedRows, reason, mode);
    // Groups are keyed by asin+marketplace+service, so success and
    // failure are tracked at that granularity too. Counting at the
    // ASIN level would silently under-report when a single ASIN has
    // both an LVS and a Hattrick group and only one of them fails.
    let successCount = 0;
    let failureCount = 0;
    const failureMessages: string[] = [];

    // Up to `MAX_BULK_PASS_EXCEPTION_ASINS` × 2 services = ~2000
    // groups. Sequential submission would hang the modal on large
    // selections, so run with bounded concurrency. JS single-threaded
    // execution keeps the shared counter / array mutations safe.
    const tasks = groups.map((group) => async () => {
      const asinStub = {
        asin: group.key.asin,
        obfuscatedMarketplaceId: group.key.obfuscatedMarketplaceId,
        marketplace: group.key.marketplace,
        validationRequestLVS:
          group.key.service === "LVS" ? group.key.validationId : "",
        validationRequestHattrick:
          group.key.service === "Hattrick" ? group.key.validationId : "",
      } as unknown as FullAsinWithAudits;

      try {
        await createAsinMarketplaceValidationDetailPassExceptions(
          asinStub,
          group.steps,
          group.key.service
        );
        successCount += 1;
      } catch (err: any) {
        failureCount += 1;
        const detail =
          err?.response?.data?.message?.toString() ??
          err?.message ??
          "unknown error";
        failureMessages.push(
          `${group.key.asin}/${group.key.service}: ${detail}`
        );
      }
    });
    await runWithConcurrency(tasks, SUBMIT_CONCURRENCY);

    const modeLabel =
      mode === "one_time" ? "One-Time Pass Exceptions" : "Pass Exceptions";
    const modeLabelLower = modeLabel.toLowerCase();
    const attemptedCount = groups.length;

    if (failureCount === 0) {
      // Confirm the write to the user. The dashboard table and its
      // dependent aggregations (Status Overview charts, Step Breakdown
      // pivot) all subscribe to `refreshFlipper`, so the
      // `triggerValidationDashboardRefresh` dispatch below repopulates
      // them without a page reload.
      dispatch(
        createNotificationDefinition(
          "success",
          `Submitted ${modeLabel}`,
          true,
          `Submitted ${modeLabelLower} for ${pluralSubmissions(successCount)}.`
        )
      );
    } else if (successCount === 0) {
      dispatch(
        createNotificationDefinition(
          "error",
          `Failed to Submit ${modeLabel}`,
          true,
          `Failed to submit ${modeLabelLower} for ${pluralSubmissions(
            attemptedCount
          )}. First failures: ` + failureMessages.slice(0, 3).join(" | ")
        )
      );
    } else {
      dispatch(
        createNotificationDefinition(
          "warning",
          `Partial ${modeLabel} Submission`,
          true,
          `Submitted ${modeLabelLower} for ${successCount} of ${pluralSubmissions(
            attemptedCount
          )}. First failures: ` + failureMessages.slice(0, 3).join(" | ")
        )
      );
    }

    dispatch(submissionFinished({ hadSuccess: successCount > 0 }));

    if (successCount > 0) {
      dispatch(triggerValidationDashboardRefresh());
    }
  };
